import { core } from '@icc/shared/helpers/b2b';
import { format } from 'date-fns';
import { DocumentNumberToken } from '../models/document-numbering.model';

export type NumberingParams = {
    offlineNumbering?: boolean;
    rootDocumentCreationDate?: Date;
    def?: Partial<Record<DocumentNumberToken, string>>;
    number?: string;
    applicationNumber?: string;
    buyerId: number;
    client: {
        reference: string;
    };
    sellerUserCode: string;
    marketCode: string;
    documentCustomCode: string;
    creationDate?: Date;
    version?: number;
    quotationVersion?: number;
    quotationNumber?: string;
    quotationNumberOrdinal?: string;
    externalId?: string;
    fromB2C?: boolean;
    userId: number;
};

export type DocumentNumberElement = {
    type: DocumentNumberToken;
    value: string;
    pad: number;
    empty: string;
    removePrev: boolean;
    b2cOfferSymbol: boolean;
};

export const tokenGenerators: Record<
    DocumentNumberToken,
    (params: NumberingParams, element: DocumentNumberElement) => string
> = {
    N_ALL: numberGen,
    N_DAY: numberGen,
    N_MONTH: numberGen,
    N_YEAR: numberGen,
    N_BUYER: numberGen,
    K_CLIENT: (params) =>
        params.client ? (params.client.reference ? params.client.reference : 'CLIENT') : '',
    K_PERSON: (params) => params.sellerUserCode,
    K_MARKET: (params) => params.marketCode,
    K_CUSTOM: (params) => (params.documentCustomCode ? params.documentCustomCode : ''),
    D_YYYY: (params) => String(params.creationDate.getFullYear()),
    D_YY: (params) => String(params.creationDate.getFullYear()).substring(2, 4),
    D_Y: (params) => String(params.creationDate.getFullYear()).substring(3, 4),
    D_MM: (params) => String(params.creationDate.getMonth() + 1),
    D_DD: (params) => String(params.creationDate.getDate()),
    V_DIGIT: (params) =>
        params.version
            ? params.version + (params.offlineNumbering ? 'C' + params.applicationNumber : '')
            : '\0V_DIGIT\0',
    V_NUMBER: (params) =>
        params.version
            ? '0abcdefghijklmnopqrstuvwxyz'.charAt(params.version) +
              (params.offlineNumbering ? 'C' + params.applicationNumber : '')
            : '\0V_NUMBER\0',
    O_NUM: (params) =>
        params.quotationNumberOrdinal != null
            ? String(params.quotationNumberOrdinal)
            : '\0QUOTANO\0',
    O_ALL: (params) => params.quotationNumber || '\0QUOTANR\0',
    O_ERP: (params) => params.externalId || '0000',
    O_V_DIGIT: (params) =>
        params.quotationVersion != null
            ? params.quotationVersion
                ? params.quotationVersion +
                  (params.offlineNumbering ? 'C' + params.applicationNumber : '')
                : ''
            : '\0QUOTA_V_DIGIT\0',
    O_V_NUMBER: (params) =>
        params.quotationVersion != null
            ? params.quotationVersion
                ? '0abcdefghijklmnopqrstuvwxyz'.charAt(params.quotationVersion) +
                  (params.offlineNumbering ? 'C' + params.applicationNumber : '')
                : ''
            : '\0QUOTA_V_NUMBER\0',
    STRING: (params, element) => element.value || '',
};

export function numberGen(params: NumberingParams, element: DocumentNumberElement) {
    return params.number
        ? params.number
        : params.offlineNumbering
        ? getOfflineNumber(element.type, params.applicationNumber, params.buyerId)
        : '\0NO\0';
}

export function getOfflineNumber(
    type: DocumentNumberToken,
    applicationNumber: string,
    sellerUserId: number
) {
    let offlineNumbers = <any>localStorage.getItem('offlineNumbers');
    if (!offlineNumbers) {
        offlineNumbers = {
            N_ALL: {},
            N_DEALER: {},
            N_YEAR: {},
            N_MONTH: {},
            N_DAY: {},
        };
    } else {
        offlineNumbers = core.parseJson(offlineNumbers);
    }

    const key: Partial<Record<DocumentNumberToken, string>> = {
        N_ALL: '',
        N_BUYER: String(sellerUserId || -1),
        N_DAY: format(new Date(), 'yyyy'),
        N_MONTH: format(new Date(), 'yyyyMM'),
        N_YEAR: format(new Date(), 'yyyyMMdd'),
    };

    Object.keys(offlineNumbers).forEach((elem) => {
        if (offlineNumbers[elem][key[elem]]) {
            offlineNumbers[elem][key[elem]]++;
        } else {
            offlineNumbers[elem][key[elem]] = 1;
        }
    });

    localStorage.setItem('offlineNumbers', core.stringJson(offlineNumbers));

    return 'C' + applicationNumber + '.' + offlineNumbers[type][key[type]];
}
